// src/pages/WeeklyRosterTab.tsx
// ─────────────────────────────────────────────────────────────────
// WEEKLY ROSTER TAB — Phase 3 implementation
// Day/Night shift toggle, week navigation, roster grid with
// colour-coded job allocations, Go/No-Go material awareness,
// push week to mobile, conflict detection
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Send, ChevronLeft, ChevronRight, RefreshCw, Sun, Moon, Layers,
  AlertTriangle, CheckCircle, Package, X, Eye, Plus, Users,
} from 'lucide-react'
import {
  getAllocations, saveAllocation, deleteAllocation, pushWeek,
  buildWeekDates, isOffDay, formatDayHeader, JOB_ALLOC_COLOUR,
} from '../services/rosterService'
import { getAllAssignableTasks, calculateGoNoGo } from '../services/taskService'
import type { DayAllocation } from '../utils/mockDataRoster'
import type { GoNoGoStatus } from '../types/mobileCrewTypes'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import { MOCK_JOBS } from '../utils/mockData'

// ── Types ─────────────────────────────────────────────────────────
type ShiftView = 'DAY' | 'NIGHT' | 'BOTH'
type WeekSpan  = 1 | 2 | 3 | 4

// ── Helpers ────────────────────────────────────────────────────────
function GoNoBadge({ status }: { status: GoNoGoStatus }) {
  const m = {
    GO:             { bg:'bg-green-100', text:'text-green-800', label:'GO'        },
    CONDITIONAL_GO: { bg:'bg-amber-100', text:'text-amber-800', label:'COND. GO'  },
    NO_GO:          { bg:'bg-red-100',   text:'text-red-800',   label:'NO GO'     },
    NO_MATERIALS:   { bg:'bg-gray-100',  text:'text-gray-500',  label:'No matls'  },
  }[status] ?? { bg:'bg-gray-100', text:'text-gray-500', label:status }
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  )
}

// ── Push Preview Modal ─────────────────────────────────────────────
function PushModal({
  shiftView, weekStart, numWeeks, onClose, onConfirm,
}: {
  shiftView: ShiftView
  weekStart: number
  numWeeks:  number
  onClose:   () => void
  onConfirm: (note: string) => void
}) {
  const [note, setNote] = useState('')
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-gray-900">Push roster to mobile</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
            <p className="font-semibold mb-1">This will push:</p>
            <p>· All {shiftView === 'BOTH' ? 'day + night' : shiftView.toLowerCase()} shift allocations</p>
            <p>· Week{numWeeks > 1 ? 's' : ''} {weekStart + 1}{numWeeks > 1 ? `–${weekStart + numWeeks}` : ''}</p>
            <p>· Including tasks and material status for each shift</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Custom message to crew (optional)
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Gate code is 1234. Start at NW corner of site. Bring hi-vis and hard hat."
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t bg-gray-50 rounded-b-2xl flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600">
            Cancel
          </button>
          <button onClick={() => onConfirm(note)}
            className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 flex items-center justify-center gap-2">
            <Send className="w-4 h-4" />Push to mobile
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main WeeklyRosterTab ───────────────────────────────────────────
export function WeeklyRosterTab({ scopedJobIds, currentUser }: {
  scopedJobIds: string[] | null
  currentUser:  any
}) {
  const [allocations, setAllocations] = useState<DayAllocation[]>([])
  const [shiftView,   setShiftView]   = useState<ShiftView>('BOTH')
  const [weekOffset,  setWeekOffset]  = useState(0)
  const [numWeeks,    setNumWeeks]    = useState<WeekSpan>(1)
  const [loading,     setLoading]     = useState(true)
  const [showPush,    setShowPush]    = useState(false)
  const [pushing,     setPushing]     = useState(false)
  const [pushToast,   setPushToast]   = useState('')
  const [taskGoNoGo,  setTaskGoNoGo]  = useState<Record<string, GoNoGoStatus>>({})
  const [conflicts,   setConflicts]   = useState<Set<string>>(new Set())

  // Week dates
  const dates = useMemo(
    () => buildWeekDates(weekOffset, numWeeks, shiftView),
    [weekOffset, numWeeks, shiftView]
  )

  // Available jobs & employees
  const availJobs = MOCK_JOBS.filter(j =>
    !['COMPLETED','CLOSED'].includes(j.status) &&
    (scopedJobIds === null || scopedJobIds.includes(j.id))
  )

  const load = useCallback(async () => {
    const [allocs, tasks] = await Promise.all([
      getAllocations(),
      getAllAssignableTasks(),
    ])
    setAllocations(allocs)

    // Go/No-Go per job
    const gnMap: Record<string, GoNoGoStatus> = {}
    for (const job of availJobs) {
      const jobTasks = tasks.filter(t => t.jobId === job.id)
      const allMaterials = jobTasks.flatMap(t => t.materialsRequired)
      gnMap[job.id] = calculateGoNoGo(allMaterials)
    }
    setTaskGoNoGo(gnMap)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  // Detect conflicts (same employee allocated to 2+ jobs on same date+shift)
  useEffect(() => {
    const keys = new Set<string>()
    const seen = new Map<string, string>()
    for (const a of allocations) {
      const key = `${a.employeeId}:${a.date}:${a.shiftType}`
      if (seen.has(key)) {
        keys.add(a.id)
        keys.add(seen.get(key)!)
      } else {
        seen.set(key, a.id)
      }
    }
    setConflicts(keys)
  }, [allocations])

  // Toggle allocation on cell click
  async function handleCellClick(empId: string, jobId: string, date: string) {
    if (isOffDay(date, shiftView)) return
    const existing = allocations.find(
      a => a.employeeId === empId && a.jobId === jobId &&
           a.date === date && a.shiftType === (shiftView === 'BOTH' ? 'DAY' : shiftView)
    )
    if (existing) {
      await deleteAllocation(existing.id)
      setAllocations(prev => prev.filter(a => a.id !== existing.id))
    } else {
      const saved = await saveAllocation({
        employeeId:      empId,
        jobId,
        date,
        shiftType:       shiftView === 'BOTH' ? 'DAY' : shiftView,
        allocatedById:   currentUser?.id ?? 'u_pm',
        allocatedByName: currentUser?.fullName ?? 'PM',
        notes:           null,
      })
      setAllocations(prev => [...prev, saved])
    }
  }

  async function handlePushWeek(customNote: string) {
    setPushing(true)
    setShowPush(false)
    try {
      await pushWeek({
        dates,
        shiftView,
        pushedByName: currentUser?.fullName ?? 'PM',
        pushedById:   currentUser?.id ?? 'u_pm',
        customNote,
      })
      setPushToast('✓ Roster pushed to all crew devices')
      setTimeout(() => setPushToast(''), 3000)
    } finally {
      setPushing(false)
    }
  }

  // Count allocated shifts in view
  const allocsInView = allocations.filter(a =>
    dates.includes(a.date) &&
    (shiftView === 'BOTH' || a.shiftType === shiftView)
  )
  const totalShifts = allocsInView.length

  if (loading) return (
    <div className="py-16 flex items-center justify-center text-gray-400">
      <RefreshCw className="w-5 h-5 animate-spin mr-2" />Loading roster...
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Controls row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Shift toggle */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          {([
            { v:'DAY',   icon:<Sun  className="w-3.5 h-3.5" />, label:'Day'   },
            { v:'NIGHT', icon:<Moon className="w-3.5 h-3.5" />, label:'Night' },
            { v:'BOTH',  icon:<Layers className="w-3.5 h-3.5" />, label:'Both' },
          ] as const).map(s => (
            <button key={s.v} onClick={() => setShiftView(s.v)}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                shiftView === s.v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {s.icon}{s.label}
            </button>
          ))}
        </div>

        {/* Week navigation */}
        <div className="flex items-center gap-1.5">
          <button onClick={() => setWeekOffset(o => o - 1)}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-20 text-center">
            {weekOffset === 0 ? 'This week' : weekOffset === 1 ? 'Next week' : weekOffset === -1 ? 'Last week' : `Week ${weekOffset > 0 ? '+' : ''}${weekOffset}`}
          </span>
          <button onClick={() => setWeekOffset(o => o + 1)}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </button>
          {weekOffset !== 0 && (
            <button onClick={() => setWeekOffset(0)}
              className="text-xs text-blue-600 hover:underline px-1">
              Today
            </button>
          )}
        </div>

        {/* Span */}
        <select
          value={numWeeks}
          onChange={e => setNumWeeks(Number(e.target.value) as WeekSpan)}
          className="text-xs rounded-lg border border-gray-200 px-2 py-1.5 bg-white text-gray-700">
          {[1,2,3,4].map(n => (
            <option key={n} value={n}>{n} week{n>1?'s':''}</option>
          ))}
        </select>

        {/* Summary + push button */}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">{totalShifts} shift{totalShifts !== 1 ? 's' : ''}</span>
          {conflicts.size > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-1 rounded-full flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />{conflicts.size / 2} conflict{conflicts.size/2 !== 1 ? 's' : ''}
            </span>
          )}
          <button
            onClick={() => setShowPush(true)}
            disabled={totalShifts === 0 || pushing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-40">
            {pushing
              ? <><RefreshCw className="w-4 h-4 animate-spin" />Pushing...</>
              : <><Send className="w-4 h-4" />Push to mobile</>
            }
          </button>
        </div>
      </div>

      {/* Push toast */}
      {pushToast && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-semibold flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />{pushToast}
        </div>
      )}

      {/* Conflict warning */}
      {conflicts.size > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Scheduling conflict detected</strong> — one or more crew members are allocated to multiple jobs on the same day and shift. Cells with conflicts are highlighted in amber.
          </div>
        </div>
      )}

      {/* Go/No-Go legend */}
      {availJobs.some(j => taskGoNoGo[j.id]) && (
        <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500">
          <span className="font-medium">Material status:</span>
          {availJobs.map(j => {
            const gng = taskGoNoGo[j.id]
            if (!gng) return null
            return (
              <div key={j.id} className="flex items-center gap-1.5">
                <span className="font-medium text-gray-700">{j.jobNumber}</span>
                <GoNoBadge status={gng} />
              </div>
            )
          })}
        </div>
      )}

      {/* Roster grid */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {/* Employee column */}
                <th className="text-left px-4 py-2.5 font-semibold text-gray-600 sticky left-0 bg-gray-50 z-10 min-w-36">
                  Employee
                </th>
                {/* Job columns (one per job) */}
                {availJobs.map(j => (
                  <th key={j.id} className="px-2 py-1.5 text-center min-w-10">
                    <div className="font-semibold text-gray-600">{j.jobNumber}</div>
                    <GoNoBadge status={taskGoNoGo[j.id] ?? 'NO_MATERIALS'} />
                  </th>
                ))}
                {/* Date columns */}
                {dates.map(d => {
                  const off = isOffDay(d, shiftView)
                  return (
                    <th key={d} className={`px-1 py-1.5 text-center min-w-9 font-medium ${off ? 'text-gray-300' : 'text-gray-600'}`}>
                      <div>{formatDayHeader(d, dates.length)}</div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {MOCK_EMPLOYEES.map((emp: any) => {
                const empAllocs = allocations.filter(a =>
                  a.employeeId === emp.id &&
                  dates.includes(a.date) &&
                  (shiftView === 'BOTH' || a.shiftType === shiftView)
                )
                const shiftCount = empAllocs.length

                return (
                  <tr key={emp.id} className={shiftCount > 0 ? 'bg-blue-50/20' : ''}>
                    {/* Employee cell */}
                    <td className="px-4 py-2 sticky left-0 bg-white z-10 border-r border-gray-100">
                      <div className="font-semibold text-gray-900 truncate max-w-32">{emp.fullName}</div>
                      <div className="text-gray-400 truncate">{emp.payClassification?.replace(/_/g,' ')}</div>
                    </td>

                    {/* Per-job allocation counts */}
                    {availJobs.map(j => {
                      const jobEmpAllocs = empAllocs.filter(a => a.jobId === j.id)
                      const colour = JOB_ALLOC_COLOUR[j.id] ?? 'bg-gray-200'
                      return (
                        <td key={j.id} className="px-2 py-2 text-center">
                          {jobEmpAllocs.length > 0 && (
                            <span className={`inline-block w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center ${colour}`}>
                              {jobEmpAllocs.length}
                            </span>
                          )}
                        </td>
                      )
                    })}

                    {/* Date cells */}
                    {dates.map(d => {
                      const off       = isOffDay(d, shiftView)
                      const dayAllocs = empAllocs.filter(a => a.date === d)
                      const hasConflict = dayAllocs.some(a => conflicts.has(a.id))

                      if (off) {
                        return <td key={d} className="px-1 py-2 bg-gray-50" />
                      }

                      return (
                        <td key={d} className="px-1 py-2">
                          {dayAllocs.length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {dayAllocs.map(a => {
                                const jColour = JOB_ALLOC_COLOUR[a.jobId] ?? 'bg-gray-400'
                                return (
                                  <button key={a.id}
                                    onClick={() => handleCellClick(emp.id, a.jobId, d)}
                                    className={`w-full h-6 rounded text-white text-xs font-bold hover:opacity-80 transition-opacity ${jColour} ${hasConflict ? 'ring-2 ring-amber-400' : ''}`}
                                    title={`${MOCK_JOBS.find(j=>j.id===a.jobId)?.jobNumber} — click to remove`}
                                  />
                                )
                              })}
                            </div>
                          ) : (
                            // Empty cell — click to open job picker
                            <JobPickerCell
                              empId={emp.id}
                              date={d}
                              availJobs={availJobs}
                              shiftView={shiftView}
                              onSelect={handleCellClick}
                            />
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Push modal */}
      {showPush && (
        <PushModal
          shiftView={shiftView}
          weekStart={weekOffset}
          numWeeks={numWeeks}
          onClose={() => setShowPush(false)}
          onConfirm={handlePushWeek}
        />
      )}
    </div>
  )
}

// ── Job Picker Cell ────────────────────────────────────────────────
function JobPickerCell({
  empId, date, availJobs, shiftView, onSelect,
}: {
  empId:     string
  date:      string
  availJobs: typeof MOCK_JOBS
  shiftView: ShiftView
  onSelect:  (empId: string, jobId: string, date: string) => void
}) {
  const [open, setOpen] = useState(false)

  if (!open) return (
    <button
      onClick={() => setOpen(true)}
      className="w-full h-6 rounded border border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
    />
  )

  return (
    <div className="relative z-20">
      <div className="absolute left-0 top-0 bg-white border border-gray-200 rounded-xl shadow-lg p-1 min-w-32 z-20">
        {availJobs.map(j => (
          <button key={j.id}
            onClick={() => { onSelect(empId, j.id, date); setOpen(false) }}
            className={`w-full text-left px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50 flex items-center gap-2`}>
            <span className={`w-3 h-3 rounded-full ${JOB_ALLOC_COLOUR[j.id] ?? 'bg-gray-300'}`} />
            {j.jobNumber}
          </button>
        ))}
        <button onClick={() => setOpen(false)} className="w-full text-center text-xs text-gray-400 py-1 hover:text-gray-600">
          Cancel
        </button>
      </div>
    </div>
  )
}
